import { apiClient } from '@/services/api-client';

/**
 * E-consent state — API-first, NO browser persistence (per Rock, 2026-07-23).
 *
 * The server (`GET /api/v1/borrower/me` → `hasAcceptedEConsent`) is the ONLY
 * source of truth: a borrower logging in from a different browser/device must
 * get the correct answer, and a stale local cache must never bypass (or
 * re-show) the compliance gate. Consent status lives in an in-memory,
 * per-session store — a page refresh simply re-asks the API.
 *
 * The ONLY storage key retained is `econsent_returnUrl` (sessionStorage),
 * used to send the borrower back to their pre-login destination.
 */

export type ConsentStatus = 'unknown' | 'consented' | 'unconsented';

let currentUserId: string | null = null;
let currentStatus: ConsentStatus = 'unknown';
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

function normalise(userId: string): string {
  return userId.toLowerCase();
}

/** Subscribe to consent-status changes (for useSyncExternalStore). */
export function subscribeConsent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Current in-memory consent status for the given user ('unknown' until the API answered). */
export function getConsentStatus(userId: string | undefined): ConsentStatus {
  if (!userId || currentUserId !== normalise(userId)) return 'unknown';
  return currentStatus;
}

/** True only when the API (or an accept POST) confirmed consent this session. */
export function hasEConsent(userId: string | undefined): boolean {
  return getConsentStatus(userId) === 'consented';
}

/** Record a consent answer for the session (in memory only). */
export function setConsentStatus(userId: string, status: ConsentStatus): void {
  currentUserId = normalise(userId);
  currentStatus = status;
  notify();
}

/** Mark consent accepted for this session (after the accept POST succeeds). */
export function setEConsent(userId: string): void {
  setConsentStatus(userId, 'consented');
}

/** Reset (logout / user switch) — next login re-asks the API. */
export function clearEConsent(_userId?: string): void {
  currentUserId = null;
  currentStatus = 'unknown';
  notify();
}

/**
 * One-time migration sweep: purge every legacy e-consent cache key from prior
 * builds so an old cached "accepted" can never bypass the gate. Keeps
 * `econsent_returnUrl` (needed for the post-consent redirect).
 */
export function purgeLegacyConsentStorage(): void {
  if (typeof window === 'undefined') return;
  // Enumerate via length/key(i) — works with both real Storage and the test
  // StorageMock (whose entries aren't visible to Object.keys).
  const collect = (s: Storage, keep?: string): string[] => {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith('econsent_') && k !== keep) keys.push(k);
    }
    return keys;
  };
  try {
    collect(localStorage).forEach((k) => localStorage.removeItem(k));
    collect(sessionStorage, 'econsent_returnUrl').forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Ask the API whether the borrower has accepted e-consent and record the
 * answer in the session store. Fails CLOSED: any error counts as
 * not-consented (the gate shows /e-consent; re-accepting is harmless — the
 * server upserts the audit record).
 */
export async function checkConsentWithApi(userId: string): Promise<boolean> {
  try {
    // Canonical check per API team (NP-59, 2026-07-23): GET /api/v1/borrower/me
    // → hasAcceptedEConsent; legacy /borrower/me/econsent fields accepted as
    // fallback for older API deploys.
    // Bounded: 10s cap + no retries. The apiClient's default is 30s with up
    // to 3 retries — a hung/cold API would pin the login loader for ~90s.
    // Worst case here is a 10s loader, then fail-closed to /e-consent.
    const timeoutSignal =
      typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
        ? AbortSignal.timeout(10_000)
        : undefined;
    const response = await apiClient.get<{
      hasAcceptedEConsent?: boolean;
      accepted?: boolean;
      acceptedAtUtc?: string | null;
    }>('/api/v1/borrower/me', { retries: 0, signal: timeoutSignal });

    const consented = Boolean(
      response?.hasAcceptedEConsent || response?.accepted || response?.acceptedAtUtc,
    );
    setConsentStatus(userId, consented ? 'consented' : 'unconsented');
    return consented;
  } catch (err) {
    console.warn('[consent] API consent check failed — failing closed (not consented):', err);
    setConsentStatus(userId, 'unconsented');
    return false;
  }
}
